import { t } from "i18next";
import { FC, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

// Utilities
import { roundNumber } from "@/_helpers/number_functions";

// Services and interfaces
import { IRecipe } from "@/interfaces/recipe/recipe";
import { IRecipeIngredient } from "@/interfaces/recipe/recipe_builder";

// Components
import Button from "../button";
import AddModal from "./add_modal";
import MacroHeader from "./macro_header";
import DetailsPane from "./details_pane";
import { Add, WarningFilled } from "@carbon/icons-react";
import BuilderTable from "../builder/table";
import InstructionsBuilder from "./instruction_builder";
import useIngredientHeaders from "./ingredients_headers";
import { Box, Container, Tab, Tabs } from "@mui/material";
import { calculateCalories } from "@/_helpers/nutrition_helper";


interface _RecipeBuilderProps {
    readonly?: boolean;
    isLoading: boolean;
    recipeImage?: string|null;
}

const RecipeBuilder: FC<_RecipeBuilderProps> = ({
    readonly = false,
    isLoading,
    recipeImage
}) => {

    const [currentTab, setCurrentTab] = useState<number>(0);
    const [open, setOpen] = useState<'add'|null>(null);
    const { control, watch, setValue, formState: { errors } } = useFormContext<IRecipe>();

    const ingredients = watch('ingredients')
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'ingredients'
    });

    const currentIngredients = ingredients.map(f => (f as IRecipeIngredient).source_ingredient_id);
    const headers = useIngredientHeaders({onRemoveIngredient: remove, readonly});

    const instructionsMethods = useFieldArray({ control, name: 'instructions' });
    const appendInstruction = () => instructionsMethods.append({
        step: instructionsMethods.fields.length + 1,
        instruction: ""
    });

    const onRemove = (index: number) => {
        instructionsMethods.fields.forEach((_,i) => {
            setValue(`instructions.${i}.step`, i < index ? i+1 : i)
        }) 
        instructionsMethods.remove(index)
    }

    useEffect(() => {
        if (!ingredients || ingredients.length === 0) return;
        const protein = roundNumber(ingredients.reduce((acc, ingredient) => {
            return acc + (ingredient.macros.protein / 100) * (ingredient.amount * ingredient.unit.weight)
        }, 0), 1);
        const carbs = roundNumber(ingredients.reduce((acc, ingredient) => {
            return acc + (ingredient.macros.carbs / 100) * (ingredient.amount * ingredient.unit.weight)
        }, 0), 1);
        const fat = roundNumber(ingredients.reduce((acc, ingredient) => {
            return acc + (ingredient.macros.fat / 100) * (ingredient.amount * ingredient.unit.weight)
        }, 0), 1);
        const fibre = roundNumber(ingredients.reduce((acc, ingredient) => {
            return acc + (ingredient.macros.fibre / 100) * (ingredient.amount * ingredient.unit.weight)
        }, 0), 1);
        const alcohol = roundNumber(ingredients.reduce((acc, ingredient) => {
            return acc + (ingredient.macros.alcohol / 100) * (ingredient.amount * ingredient.unit.weight)
        }, 0), 1);
        const calories = calculateCalories(protein, carbs, fat);

        setValue('calories', calories);
        setValue('protein', protein);
        setValue('carbs', carbs);
        setValue('fat', fat);
        setValue('fibre', fibre);
        setValue('alcohol', alcohol);
    }, [ingredients]);

    return (
        <Box height='100%' overflow='auto'>
            <Container sx={{
                py: 4,
                textAlign: 'left',
                display: 'flex',
                gap: 3
                }}>
                    
                {/* SECTION: RECIPE DETAILS */}
                <Box flexShrink={1} width='100%' maxWidth='320px'>
                    <div className="heading-07-compact text-secondary" style={{margin: '8px 0'}}>
                        {t('components.recipeBuilder.recipeDetails')}
                    </div>
                    <DetailsPane readonly={readonly} isLoading={isLoading} recipeImage={recipeImage} />
                </Box>

                {/* SECTION: MAIN */}
                <Box width='100%'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <span className="heading-07-compact text-secondary" style={{
                            margin: '8px 0'
                        }}>{t('components.recipeBuilder.recipe')}</span>
                        {/* <Button 
                            kind="ghost"
                            size='small'
                            label={t('components.buttons.viewMicronutrients')}
                            endIcon={<View />}
                        /> */}
                    </Box>
                    
                    <MacroHeader isLoading={isLoading} />

                    {/* Ingredients table */}
                    <Box 
                        mt={1} py={2}
                        boxSizing="border-box"
                        borderRadius="6px" 
                        border="solid 1px var(--border-subtle-01)" 
                        bgcolor="var(--layer-01)" 
                        >
                        <Box px={3} display='flex' justifyContent='space-between' alignItems='center' borderBottom='solid 1px var(--border-subtle-01)'>
                            <Tabs className="Tabs" value={currentTab} onChange={(_, val) => setCurrentTab(val)} sx={{mx: 0}}>
                                <Tab 
                                    value={0}
                                    className="Tabs__tab"
                                    label={t('components.recipeBuilder.ingredients')}
                                />
                                <Tab 
                                    value={1}
                                    className="Tabs__tab"
                                    label={t('components.recipeBuilder.instructions')}
                                />
                            </Tabs>

                            {currentTab == 0 && <Button 
                                kind='ghost' 
                                label={t('components.buttons.addFood')}
                                endIcon={<Add />}
                                minWidth={false}
                                disabled={readonly}
                                onClick={() => setOpen('add')}
                            />}

                            {currentTab == 1 && <Button
                                kind='ghost' 
                                label={t('components.buttons.addStep')}
                                endIcon={<Add />}
                                minWidth={false}
                                disabled={readonly}
                                onClick={appendInstruction}
                            />}
                        </Box>

                        <Box px={3}>
                            {currentTab == 0 && <BuilderTable
                                dataKey="id"
                                data={fields}
                                columns={headers}
                                isLoading={isLoading}
                                noDataMessage={<_NoIngredients hasErrors={!!errors.ingredients} />}
                                />}
                            
                            {currentTab == 1 && <InstructionsBuilder
                                fields={instructionsMethods.fields}
                                readonly={readonly}
                                remove={onRemove}
                                />}
                        </Box>
                        
                    </Box>
                </Box>

                {/* SECTION: MODALS */}
                {open === 'add' && <AddModal
                    open={open === 'add'}
                    selectedFoods={currentIngredients}
                    onClose={()=>setOpen(null)}
                    onAdd={(foods) => append(foods)}
                />}

            </Container>
        </Box>
    )
}

export default RecipeBuilder;

interface _NoIngredientsProps {
    hasErrors: boolean;
}
const _NoIngredients: FC<_NoIngredientsProps> = ({
    hasErrors
}) => {

    return (
        <Box display="flex" justifyContent="center" alignItems="center">
            {t(`components.recipeBuilder.errors.${hasErrors ? 'noIngredientsError' : 'noIngredients'}`)}
            {hasErrors && <WarningFilled size="12px" style={{color: 'var(--support-error)', marginLeft: '8px'}} />}
        </Box>
    )
}